import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSnackbar, closeSnackbar, type SnackbarKey, type BaseVariant } from 'notistack';
import Button from '@mui/material/Button';
import PowerIconButton from '../components/PowerIconButton';
import { exception } from '@erikdakoda/log/exception';
/** Every snack from this hook, the undo failure included, opens in the same place. */
const SNACK_ANCHOR_ORIGIN = { vertical: 'top', horizontal: 'center' } as const;

export type SnackOptions = {
  variant?: BaseVariant;
  undo?: () => Promise<void>;
  actionLabel?: string;
  onAction?: () => void;
};

export type SnackFunctions = {
  snack: (message: string, options?: SnackOptions) => void;
  error: (message: string, options?: SnackOptions) => void;
  success: (message: string, options?: SnackOptions) => void;
  warning: (message: string, options?: SnackOptions) => void;
  info: (message: string, options?: SnackOptions) => void;
};

export const useSnack = (): { snack: SnackFunctions } => {
  'use memo';

  const { enqueueSnackbar } = useSnackbar();
  const loadingUndoRef = useRef(false);
  const [, setLoadingUndoGeneration] = useState(0);

  // keep: stable snack functions for useException and doFetch effect dependencies
  const base = useCallback(
    (message: string, { variant, undo, actionLabel, onAction }: SnackOptions = {}) => {
      const action = (snackbarId: SnackbarKey) => {
        return (
          <>
            {onAction && actionLabel ? (
              <Button
                size="medium"
                color="inherit"
                onClick={() => {
                  onAction();
                  closeSnackbar(snackbarId);
                }}
              >
                {actionLabel}
              </Button>
            ) : null}
            {undo ? (
              <Button
                data-testid="snack-undo"
                loading={loadingUndoRef.current}
                size="medium"
                color="inherit"
                onClick={async () => {
                  loadingUndoRef.current = true;
                  setLoadingUndoGeneration((generation) => generation + 1);
                  try {
                    await undo?.();
                  } catch (err: unknown) {
                    const message = exception(err);
                    enqueueSnackbar(message, {
                      variant: 'error',
                      anchorOrigin: SNACK_ANCHOR_ORIGIN,
                    });
                  } finally {
                    loadingUndoRef.current = false;
                    setLoadingUndoGeneration((generation) => generation + 1);
                  }
                  closeSnackbar(snackbarId);
                }}
              >
                Undo
              </Button>
            ) : null}
            <PowerIconButton
              icon="xmark"
              aria-label="close"
              onClick={() => {
                closeSnackbar(snackbarId);
              }}
            />
          </>
        );
      };

      enqueueSnackbar(message, {
        variant,
        autoHideDuration: undo ? 15000 : 5000,
        action,
        anchorOrigin: SNACK_ANCHOR_ORIGIN,
      });
    },
    [enqueueSnackbar],
  );

  // keep: stable reference chained from base for useException
  const snack = useCallback(
    (message: string, options: SnackOptions = {}) => {
      base(message, { variant: 'default', ...options });
    },
    [base],
  );

  // keep: stable reference chained from base for useException
  const error = useCallback(
    (message: string, options: SnackOptions = {}) => {
      base(message, { variant: 'error', ...options });
    },
    [base],
  );

  // keep: stable reference chained from base for useException
  const success = useCallback(
    (message: string, options: SnackOptions = {}) => {
      base(message, { variant: 'success', ...options });
    },
    [base],
  );

  // keep: stable reference chained from base for useException
  const warning = useCallback(
    (message: string, options: SnackOptions = {}) => {
      base(message, { variant: 'warning', ...options });
    },
    [base],
  );

  // keep: stable reference chained from base for useException
  const info = useCallback(
    (message: string, options: SnackOptions = {}) => {
      base(message, { variant: 'info', ...options });
    },
    [base],
  );

  // keep: stable snack object identity for useException dependency on snack.error
  const snackFunctions = useMemo(
    () => ({ error, success, warning, info, snack }),
    [error, info, snack, success, warning],
  );

  return { snack: snackFunctions };
};

export type UseSnackHook = ReturnType<typeof useSnack>;
